import { Controller } from "@hotwired/stimulus"
import { measure, settle } from "../modules/readout_meters"

// SETTLE readouts. Every [data-settle] cell already carries its honest server-
// baked value in the markup; this controller recomputes the same measure from the
// live DOM and animates the needle to it. Under reduced motion the value is
// written immediately (no needle). A belt-and-braces timer latches any un-seen
// cell after 3s so a readout never sits stale.
//
// Controller element: a container of [data-settle][data-measure] cells.
// data-scope="#selector" scopes the measurements (defaults to document).
// [data-now-date] children receive today's ISO date.
const FALLBACK_MS = 3000

export default class extends Controller {
  connect() {
    this._scope = (this.element.dataset.scope && document.querySelector(this.element.dataset.scope)) || document
    this._stampDates()
    this.cells = Array.from(this.element.querySelectorAll("[data-settle]"))
    this.pending = new Set(this.cells)
    if (this.cells.length === 0) return
    this._observe()
    this._fallback = setTimeout(() => this._latchRemaining(), FALLBACK_MS)
  }

  disconnect() {
    if (this._observer) this._observer.disconnect()
    if (this._fallback) clearTimeout(this._fallback)
  }

  _stampDates() {
    this.element.querySelectorAll("[data-now-date]").forEach((node) => {
      node.textContent = new Date().toISOString().slice(0, 10)
    })
  }

  _observe() {
    if (typeof IntersectionObserver !== "function") {
      this._latchRemaining() // no observer: latch honest values now, no needle
      return
    }
    this._observer = new IntersectionObserver((entries) => {
      entries.forEach((entry) => {
        if (entry.isIntersecting) this._fire(entry.target)
      })
    }, { threshold: 0.15 })
    this.cells.forEach((cell) => this._observer.observe(cell))
  }

  _fire(cell) {
    if (!this.pending.has(cell)) return
    this.pending.delete(cell)
    this._observer?.unobserve(cell)
    settle(cell, measure(cell.dataset.measure, this._scope), { reducedMotion: this._reducedMotion() })
  }

  _latchRemaining() {
    Array.from(this.pending).forEach((cell) => {
      cell.textContent = measure(cell.dataset.measure, this._scope).toLocaleString("en")
      this.pending.delete(cell)
      this._observer?.unobserve(cell)
    })
  }

  _reducedMotion() {
    return typeof window.matchMedia === "function" &&
      window.matchMedia("(prefers-reduced-motion: reduce)").matches
  }
}
